import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import GalleryPage from './pages/GalleryPage';
import MessagePage from './pages/MessagePage';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import UserIdModal from './components/UserIdModal';
import { useKindnessId } from './hooks/useKindnessId';
import { checkSupabaseConnection } from './lib/supabase';
import { migrateLocalMessagesToSupabase } from './utils/storage';
import './App.css';

function App() {
  const { kindnessId, showModal, setShowModal, setKindnessId } = useKindnessId();
  const [supabaseStatus, setSupabaseStatus] = useState({
    checked: false,
    connected: false,
    error: null,
    migratedCount: 0
  });

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const status = await checkSupabaseConnection();
        setSupabaseStatus(prev => ({
          ...prev,
          checked: true,
          connected: status.connected,
          error: status.error
        }));
        
        if (!status.connected) {
          console.error('Supabase connection issue:', status.error);
        } else {
          console.log('Supabase connected successfully');
          
          // Try to migrate local messages to Supabase
          try {
            const { migrated, error } = await migrateLocalMessagesToSupabase();
            if (error) {
              console.error('Error migrating messages:', error);
            } else if (migrated > 0) {
              console.log(`Successfully migrated ${migrated} messages to Supabase`);
              setSupabaseStatus(prev => ({
                ...prev,
                migratedCount: migrated
              }));
            }
          } catch (migrateErr) {
            console.error('Failed to migrate messages:', migrateErr);
          }
        }
      } catch (err) {
        console.error('Failed to check Supabase connection:', err);
        setSupabaseStatus({
          checked: true,
          connected: false,
          error: err.message,
          migratedCount: 0
        });
      }
    };
    
    checkConnection();
  }, []);

  return (
    <Router>
      <div className="min-h-screen bg-background">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col min-h-screen"
        >
          <Header kindnessId={kindnessId} onShowModal={() => setShowModal(true)} />
          <main className="flex-1">
            {!supabaseStatus.checked ? (
              <div className="max-w-4xl mx-auto px-4 py-8 text-center">
                <div className="clay-card">
                  <p className="text-text">Connecting to database...</p>
                </div>
              </div>
            ) : !supabaseStatus.connected ? (
              <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="clay-card bg-pink">
                  <h3 className="text-xl font-bold text-heading mb-2">Database Connection Issue</h3>
                  <p className="text-text">
                    We're having trouble connecting to our database. Your messages will be stored locally for now.
                  </p>
                  {supabaseStatus.error && (
                    <p className="text-sm text-red-700 mt-2">Error: {supabaseStatus.error}</p>
                  )}
                </div>
              </div>
            ) : supabaseStatus.migratedCount > 0 ? (
              <div className="max-w-4xl mx-auto px-4 py-4">
                <div className="clay-card bg-green">
                  <h3 className="text-xl font-bold text-heading mb-2">Messages Synchronized</h3>
                  <p className="text-text">
                    Successfully synchronized {supabaseStatus.migratedCount} messages with the database.
                  </p>
                </div>
              </div>
            ) : null}
            <Routes>
              <Route path="/" element={<Home kindnessId={kindnessId} setKindnessId={setKindnessId} />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/message/:id" element={<MessagePage />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            </Routes>
          </main>
          <Footer />
        </motion.div>
        {showModal && (
          <UserIdModal
            onClose={() => setShowModal(false)}
            onSetId={setKindnessId}
            currentId={kindnessId}
          />
        )}
      </div>
    </Router>
  );
}

export default App;